/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * Bu dosya QubePro ekosisteminde ortak (global) standarttır.
 * Tek kaynak `qubepro-core` reposudur; uydu repolarda kalıcı değişiklik yapılmaz.
 * Değişiklik gerekiyorsa Core'da güncellenir ve senkron ile diğer repolara yayılır.
 */

/**
 * API hata yönetimi utility fonksiyonları
 */

import { AxiosError } from 'axios';
import { DEFAULT_LANGUAGE, type SupportedLanguage } from './language.utils';
import { tr } from '../locales/tr';
import { en } from '../locales/en';
import { ar } from '../locales/ar';
import { de } from '../locales/de';
import { es } from '../locales/es';
import { fr } from '../locales/fr';
import { it } from '../locales/it';
import { ja } from '../locales/ja';
import { ru } from '../locales/ru';
import { zh } from '../locales/zh';

const translations: Record<SupportedLanguage, Record<string, any>> = {
  tr, en, ar, de, es, fr, it, ja, ru, zh
};

function getTranslations(language: string): Record<string, any> {
  const lang = language.toLowerCase().substring(0, 2) as SupportedLanguage;
  return translations[lang] || translations[DEFAULT_LANGUAGE];
}

/** Tek bir Zod issue veya benzeri nesneyi okunabilir metne çevirir */
function formatSingleApiValidationEntry(issue: unknown): string {
  if (issue == null) {
    return '';
  }
  if (typeof issue === 'string') {
    return issue;
  }
  if (typeof issue === 'object' && issue !== null && 'message' in issue) {
    const msg = (issue as { message: unknown }).message;
    if (typeof msg !== 'string') {
      try {
        return JSON.stringify(issue);
      } catch {
        return '';
      }
    }
    const path = (issue as { path?: unknown }).path;
    const pathStr =
      Array.isArray(path) && path.length > 0
        ? path.map((p) => String(p)).filter((p) => p !== '')
        : [];
    const pathLabel = pathStr.length > 0 ? pathStr.join('.') : '';
    return pathLabel ? `${pathLabel}: ${msg}` : msg;
  }
  try {
    return JSON.stringify(issue);
  } catch {
    return String(issue);
  }
}

/**
 * API'den gelen `errors`: Zod `issues` dizisi veya alan adı → string | string[] haritası.
 */
export function formatApiValidationErrors(errors: unknown): string {
  if (errors == null) {
    return '';
  }
  if (Array.isArray(errors)) {
    return errors
      .map((issue) => formatSingleApiValidationEntry(issue))
      .filter((s) => s.length > 0)
      .join('; ');
  }
  if (typeof errors !== 'object') {
    return String(errors);
  }
  return Object.entries(errors as Record<string, unknown>)
    .map(([field, messages]) => {
      if (Array.isArray(messages)) {
        const parts = messages.map((m) => formatSingleApiValidationEntry(m));
        return `${field}: ${parts.filter((p) => p.length > 0).join(', ')}`;
      }
      if (messages == null || messages === '') {
        return '';
      }
      if (typeof messages === 'string') {
        return `${field}: ${messages}`;
      }
      const single = formatSingleApiValidationEntry(messages);
      return single ? `${field}: ${single}` : '';
    })
    .filter((s) => s.length > 0)
    .join('; ');
}

/** Liste / toast için ilk anlamlı doğrulama mesajı */
export function getFirstApiValidationErrorMessage(errors: unknown): string | null {
  if (errors == null) {
    return null;
  }
  if (Array.isArray(errors) && errors.length > 0) {
    const s = formatSingleApiValidationEntry(errors[0]);
    return s || null;
  }
  if (typeof errors === 'object' && !Array.isArray(errors)) {
    const keys = Object.keys(errors as object);
    if (keys.length === 0) {
      return null;
    }
    const firstKey = keys[0];
    const firstVal = (errors as Record<string, unknown>)[firstKey];
    if (Array.isArray(firstVal) && firstVal.length > 0) {
      const el = firstVal[0];
      if (typeof el === 'string') {
        return el;
      }
      return formatSingleApiValidationEntry(el) || null;
    }
    if (typeof firstVal === 'string') {
      return firstVal;
    }
    return formatSingleApiValidationEntry(firstVal) || null;
  }
  return null;
}

/**
 * Axios hata objesinden kullanıcı dostu hata mesajı çıkarır
 * @param error - Axios error veya genel error
 * @param language - Dil tercihi
 * @returns Kullanıcı dostu hata mesajı
 */
export function getErrorMessage(error: unknown, language: string = DEFAULT_LANGUAGE): string {
  const t = getTranslations(language);

  if (!error) {
    return t.errors.unknown;
  }

  // Axios error kontrolü
  if (error instanceof Error && 'isAxiosError' in error) {
    const axiosError = error as AxiosError<any>;

    // Network hatası - sunucuya bağlanılamadı
    if (!axiosError.response) {
      // userMessage varsa onu kullan (interceptor'dan geliyor)
      if ((axiosError as any).userMessage) {
        return (axiosError as any).userMessage;
      }

      // Error code kontrolü
      if (axiosError.code === 'ERR_NETWORK' || axiosError.code === 'ECONNABORTED') {
        return t.errors.network;
      }

      // Timeout hatası
      if (axiosError.message?.includes('timeout') || axiosError.code === 'ETIMEDOUT') {
        return t.errors.timeout;
      }

      // Genel network hatası
      return t.errors.network;
    }

    // HTTP status kodlarına göre mesajlar
    const status = axiosError.response.status;
    const errorData = axiosError.response.data;

    // Backend'den gelen hata mesajı varsa onu kullan
    if (errorData?.error) {
      return errorData.error;
    }

    // Status kodlarına göre varsayılan mesajlar
    switch (status) {
      case 400:
        return t.errors.badRequest;
      case 401:
        return t.errors.unauthorized;
      case 403:
        return t.errors.forbidden;
      case 404:
        return t.errors.notFound;
      case 408:
        return t.errors.timeout;
      case 429:
        return t.errors.tooManyRequests;
      case 500:
        return t.errors.internalServerError;
      case 502:
        return t.errors.badGateway;
      case 503:
        return t.errors.serviceUnavailable;
      case 504:
        return t.errors.gatewayTimeout;
      default:
        return t.errors.serverErrorWithStatus.replace('{status}', String(status));
    }
  }

  // Genel Error objesi
  if (error instanceof Error) {
    // If message already contains extended/Latin script characters, return as-is (likely localized)
    if (/[çğıöşüÇĞIİÖŞÜ]/.test(error.message)) {
      return error.message;
    }

    return error.message || t.errors.unknown;
  }

  // String hatası
  if (typeof error === 'string') {
    return error;
  }

  // Diğer durumlar
  return t.errors.unknown;
}

/**
 * Validation hatalarını formatlar
 * @param error - Axios error
 * @param language - Dil tercihi
 * @returns Formatlanmış validation hata mesajı
 */
export function getValidationErrorMessage(error: unknown, language: string = DEFAULT_LANGUAGE): string {
  if (error instanceof Error && 'isAxiosError' in error) {
    const axiosError = error as AxiosError<any>;
    const errorData = axiosError.response?.data;
    const rawErrors = errorData?.errors;

    if (rawErrors != null && (Array.isArray(rawErrors) || typeof rawErrors === 'object')) {
      const errorDetails = formatApiValidationErrors(rawErrors);
      const baseMessage = getErrorMessage(error, language);
      if (errorDetails) {
        return `${baseMessage} - ${errorDetails}`;
      }
    }
  }

  return getErrorMessage(error, language);
}

/**
 * Network hatası olup olmadığını kontrol eder
 * @param error - Error objesi
 * @returns Network hatası ise true
 */
export function isNetworkError(error: unknown): boolean {
  if (error instanceof Error && 'isAxiosError' in error) {
    const axiosError = error as AxiosError<any>;
    return !axiosError.response ||
      axiosError.code === 'ERR_NETWORK' ||
      axiosError.code === 'ECONNABORTED' ||
      axiosError.code === 'ETIMEDOUT';
  }
  return false;
}
